import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, CalendarDays, MapPin, DollarSign, Info, Map,
  Ticket, Bell, Luggage, Utensils,
  Plane, BedDouble, AlertTriangle, ChevronRight, ChevronLeft, Download, Image, BookImage, Icon, Upload,
} from '../ui/Icon';
import { downloadTripPdf } from '../pdf/TripPdfDocument';
import ImportDocumentModal from './ImportDocumentModal';
import EditableTripName from './EditableTripName';
import EditableTripDates from './EditableTripDates';
import EditableTripDestinations from './EditableTripDestinations';
import { differenceInDays } from 'date-fns';
import { getIataCode } from '../../utils/iata';
import { getDest, photoUrl } from '../../utils/destinations';
import type { ItineraryDay, Trip } from '../../types';
import { tripsApi } from '../../api/trips.api';
import { toast } from '../ui/Toast';
import { useTripContext } from '../../pages/trip/TripDetailPage';
import { hidesTab } from '../../utils/tripPermissions';
import { useThemeStore } from '../../stores/theme.store';
import { tripMemberCount } from '../../utils/tripMembers';
import { PhotoCredit } from '../ui/PhotoCredit';
import AgencyCard from '../agency/AgencyCard';

const DEST_COLORS: [string, string][] = [
  ['paris',    '#f43f5e'], ['london',  '#475569'], ['new york', '#f59e0b'],
  ['lisboa',   '#0d9488'], ['lisbon',  '#0d9488'], ['madrid',   '#ef4444'],
  ['barcelona','#f97316'], ['rome',    '#d97706'], ['tokyo',    '#ec4899'],
  ['dubai',    '#eab308'], ['amsterdam','#0ea5e9'],
];
function getDestColor(dest: string): string {
  const lower = dest.toLowerCase();
  return DEST_COLORS.find(([k]) => lower.includes(k))?.[1] ?? '#0d9488';
}

interface Notification { id: string; urgent: boolean; type: string; title: string; subtitle: string; }

interface Props {
  trip: Trip;
  onTripUpdate: (t: Trip) => void;
  /** Changing the dates re-anchors the itinerary, so its days come back too. */
  onDaysChanged: (days: ItineraryDay[]) => void;
  /** Owners and editors rename the trip from the card; viewers just read it. */
  canEdit: boolean;
  notifications: Notification[];
  /** The participants modal lives in TripDetailPage: the sidebar is hidden on
   *  mobile, so the mobile header needs the same entry point. */
  onManageParticipants: () => void;
}

function useNav() {
  const { t } = useTranslation();
  return [
    { to: 'itinerary', label: t('trip.nav.itinerary'), NavIcon: CalendarDays },
    { to: 'venues',    label: t('trip.nav.places'),    NavIcon: MapPin       },
    { to: 'map',       label: t('trip.nav.map'),       NavIcon: Map          },
    { to: 'bookings',  label: t('trip.nav.bookings'),  NavIcon: Ticket       },
    { to: 'expenses',  label: t('trip.nav.expenses'),  NavIcon: DollarSign   },
    { to: 'meals',     label: t('trip.nav.meals'),     NavIcon: Utensils     },
    { to: 'packing',   label: t('trip.nav.packing'),   NavIcon: Luggage      },
    { to: 'info',      label: t('trip.nav.info'),      NavIcon: Info         },
    { to: 'notes',     label: t('trip.nav.notes'),     NavIcon: BookOpen     },
    { to: 'gallery',   label: t('trip.nav.gallery'),   NavIcon: Image        },
    { to: 'album',     label: t('trip.nav.album'),     NavIcon: BookImage    },
  ];
}

export default function TripSidebar({ trip, onTripUpdate, onDaysChanged, canEdit, notifications, onManageParticipants }: Props) {
  const { t } = useTranslation();
  const NAV = useNav().filter(item => !hidesTab(trip, item.to)); // ADR-0038

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');
  const [notifOpen, setNotifOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  }

  const { reload, loadDays } = useTripContext();
  const { isDark } = useThemeStore();
  const destColor = getDestColor(trip.destination);
  const destPhoto = getDest(trip.destination).photo;
  const photoSrc = trip.coverImageUrl || (destPhoto ? photoUrl(destPhoto, 600) : '');
  const iata = getIataCode(trip.destination);
  const watermark = trip.destination[0]?.toUpperCase() ?? '';
  const memberCount = tripMemberCount(trip);

  const daysUntil = differenceInDays(new Date(trip.startDate), new Date());
  const isPast = new Date(trip.endDate) < new Date();
  const totalDays = differenceInDays(new Date(trip.endDate), new Date(trip.startDate)) + 1;
  const bookingCount = (trip.flights?.length ?? 0) + (trip.hotels?.length ?? 0);
  const readyPct = Math.min(100, Math.round((bookingCount / Math.max(1, totalDays * 0.5)) * 100));

  return (
    <>
      <div className={`group/sidebar hidden md:flex shrink-0 h-full relative transition-all duration-200 ${collapsed ? 'w-14' : 'w-[260px]'}`}>
        {/* ── Toggle pill on right border (hover only) ── */}
        <button
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shadow-sm opacity-0 group-hover/sidebar:opacity-100 transition-opacity cursor-pointer"
        >
          <Icon as={collapsed ? ChevronRight : ChevronLeft} size={12} />
        </button>
      <aside className={`flex flex-col w-full h-full bg-white dark:bg-slate-900 border-r border-slate-200/60 dark:border-slate-800 ${collapsed ? '' : 'overflow-y-auto'}`}>

        {collapsed ? (
          /* ── Collapsed: icon-only rail ── */
          <div className="flex flex-col h-full pt-3">

            {/* Nav icons */}
            <nav className="flex-1 flex flex-col items-center gap-1 px-2">
              {NAV.map(({ to, label, NavIcon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  data-tour={`nav-${to}`}
                  className={({ isActive }) =>
                    `relative w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                      isActive
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                        : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200'
                    }`
                  }
                >
                  {() => (
                    <>
                      <Icon as={NavIcon} size={18} />
                      {to === 'bookings' && notifications.length > 0 && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Bell icon if notifications */}
            {notifications.length > 0 && (
              <div className="flex justify-center pb-4">
                <button
                  onClick={() => { toggleCollapsed(); setNotifOpen(true); }}
                  className="relative w-10 h-10 rounded-xl flex items-center justify-center text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors cursor-pointer"
                  title={t('trip.notifications.title')}
                >
                  <Icon as={Bell} size={18} />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ── Expanded: full sidebar ── */
          <>

        {/* ── Identity card ── */}
        <div className="px-3 pt-3 pb-3">
          <div
            className="rounded-2xl overflow-hidden relative"
            style={photoSrc ? {
              backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.50), rgba(0,0,0,0.65)), url(${photoSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            } : {
              background: `linear-gradient(135deg, ${destColor}dd, ${destColor})`,
            }}
          >
            {/* dot pattern (only on gradient fallback) */}
            {!photoSrc && (
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '14px 14px' }} />
            )}
            {/* watermark */}
            <div
              className="absolute top-1 right-3 font-serif italic font-[200] leading-none pointer-events-none select-none text-white"
              style={{ fontSize: 66, opacity: 0.14 }}
            >
              {watermark}
            </div>

            {/* header 88px */}
            <div className="px-4 pt-4 pb-3 relative z-10 flex flex-col items-start leading-tight" style={{ minHeight: 88 }}>
              <span className="font-mono text-[9px] tracking-[2px] font-bold text-white/60 uppercase">
                {iata}
              </span>
              <EditableTripName
                trip={trip}
                canEdit={canEdit}
                onRenamed={onTripUpdate}
                className="font-serif font-bold text-[17px] leading-tight text-white mt-0.5"
                displayClassName="line-clamp-1"
              />
              <EditableTripDates
                trip={trip}
                canEdit={canEdit}
                onUpdated={onTripUpdate}
                onDaysChanged={onDaysChanged}
                className="font-mono text-[9px] tracking-[0.5px] text-white/60 mt-1"
              />
              <EditableTripDestinations
                trip={trip}
                canEdit={canEdit}
                onUpdated={onTripUpdate}
                className="text-[11px] text-white/75 mt-0.5 max-w-full"
              />
              {photoSrc && (
                <PhotoCredit
                  name={trip.coverImagePhotographer}
                  url={trip.coverImagePhotographerUrl}
                  className="block max-w-full mt-1"
                />
              )}
            </div>

            {/* avatar footer */}
            <div className="px-4 py-2.5 flex items-center justify-between bg-black/15 backdrop-blur-sm relative z-10">
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-1">
                  {trip.participants.slice(0, 3).map(p => (
                    <div key={p.id} className="w-5 h-5 rounded-full bg-white/30 border border-white/50 flex items-center justify-center text-white text-[9px] font-bold">
                      {(p.user.displayName || p.user.username)[0].toUpperCase()}
                    </div>
                  ))}
                </div>
                <span className="text-[10px] text-white/70">{t('trip.sidebar.travellers', { count: memberCount })}</span>
              </div>
              <button
                onClick={onManageParticipants}
                className="text-[10px] font-semibold text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                {t('trip.sidebar.manage')}
              </button>
            </div>
          </div>
        </div>

        {/* ── Countdown chip ── */}
        {!isPast && (
          <div className="mx-3 mb-3 px-3 py-2.5 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-500 text-white flex items-center justify-center font-serif italic font-bold text-lg shrink-0">
              {daysUntil > 0 ? daysUntil : '✓'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-teal-700 dark:text-teal-300">
                <Icon as={Plane} size={12} />
                <span className="text-xs font-semibold">
                  {daysUntil <= 0 ? t('trip.sidebar.underway') : daysUntil === 1 ? t('trip.sidebar.tomorrow') : t('trip.sidebar.inDays', { count: daysUntil })}
                </span>
              </div>
              <p className="text-[10px] font-mono tracking-wide text-teal-500 dark:text-teal-400 uppercase">
                {t('trip.sidebar.ready', { pct: readyPct })}
              </p>
            </div>
          </div>
        )}

        {trip.agency && (
          <div className="mx-3 mb-3">
            <AgencyCard agency={trip.agency} compact />
          </div>
        )}

        {/* ── Workspace nav ── */}
        <nav className="flex-1 px-3 pb-2 space-y-px">
          <p className="px-3 pb-2 text-[9px] font-mono tracking-[0.15em] text-slate-400 dark:text-slate-600 uppercase">
            {t('trip.sidebar.workspace')}
          </p>
          {NAV.map(({ to, label, NavIcon }) => (
            <NavLink
              key={to}
              to={to}
              data-tour={`nav-${to}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all cursor-pointer ${
                  isActive
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-200'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon as={NavIcon} size={16} className="shrink-0" />
                  <span className="flex-1 truncate">{label}</span>
                  {to === 'bookings' && notifications.length > 0 && (
                    <span className="ml-auto w-4 h-4 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                  {isActive && <Icon as={ChevronRight} size={12} className="ml-auto opacity-40" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* ── Toolbox ── */}
        <div className="px-3 pb-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
          {/* Reminders */}
          {notifications.length > 0 && (
            <button
              onClick={() => setNotifOpen(o => !o)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-800/40 dark:bg-amber-900/20 transition-colors cursor-pointer"
            >
              <Icon as={Bell} size={14} className="text-amber-600 shrink-0" />
              <span className="text-xs text-amber-700 dark:text-amber-400 flex-1 text-left">
                {t('trip.notifications.reminder', { count: notifications.length })}
              </span>
              <span className="w-4 h-4 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                {notifications.length}
              </span>
            </button>
          )}

          {notifOpen && notifications.length > 0 && (
            <div className="space-y-1.5 pb-1">
              {notifications.map(n => (
                <div key={n.id} className={`flex items-start gap-2 px-3 py-2 rounded-xl border ${n.urgent ? 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800/40' : 'bg-amber-50/60 border-amber-100 dark:bg-amber-900/10 dark:border-amber-800/30'}`}>
                  <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${n.urgent ? 'bg-red-100 dark:bg-red-900/40' : 'bg-amber-100 dark:bg-amber-900/40'}`}>
                    {n.urgent
                      ? <Icon as={AlertTriangle} size={12} className="text-red-600" />
                      : n.type === 'flight'
                        ? <Icon as={Plane} size={12} className="text-amber-600" />
                        : <Icon as={BedDouble} size={12} className="text-amber-600" />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold truncate ${n.urgent ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>{n.title}</p>
                    <p className={`text-[10px] mt-0.5 ${n.urgent ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{n.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Import from document */}
          <button
            data-tour="import-doc"
            onClick={() => setImportOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-teal-300 hover:bg-teal-50/50 dark:hover:border-teal-700 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300 transition-colors cursor-pointer"
          >
            <Icon as={Upload} size={14} className="shrink-0" />
            <span className="text-xs font-medium flex-1 text-left">{t('import.sidebarButton')}</span>
          </button>

          {/* Export PDF */}
          <button
            disabled={exportLoading}
            onClick={async () => {
              setExportLoading(true);
              try {
                const data = await tripsApi.exportData(trip.id);
                await downloadTripPdf(data);
              } catch {
                toast(t('trip.sidebar.exportFailed'), 'error');
              } finally {
                setExportLoading(false);
              }
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon as={Download} size={14} className="shrink-0" />
            <span className="text-xs font-medium flex-1 text-left">
              {exportLoading ? t('trip.sidebar.generating') : t('trip.sidebar.exportPdf')}
            </span>
          </button>

        </div>
          </>
        )}
      </aside>
      </div>

      {importOpen && (
        <ImportDocumentModal
          tripId={trip.id}
          onClose={() => setImportOpen(false)}
          onImported={() => { reload(); loadDays(); }}
        />
      )}
    </>
  );
}
